const VALIDATION_MESSAGE_TRANSLATIONS = [
  {
    match: /^Input should be a valid number$/i,
    format: () => 'introduza um número válido',
  },
  {
    match: /^Input should be a valid integer$/i,
    format: () => 'introduza um número inteiro válido',
  },
  {
    match: /^Field required$/i,
    format: () => 'campo obrigatório',
  },
  {
    match: /^Input should be a valid string$/i,
    format: () => 'introduza um texto válido',
  },
  {
    match: /^Input should be a valid boolean$/i,
    format: () => 'seleccione um valor válido',
  },
  {
    match: /^String should have at least (\d+) characters$/i,
    format: (match) => `deve ter pelo menos ${match[1]} caracteres`,
  },
  {
    match: /^String should have at most (\d+) characters$/i,
    format: (match) => `deve ter no máximo ${match[1]} caracteres`,
  },
  {
    match: /^Input should be greater than or equal to (.+)$/i,
    format: (match) => `deve ser maior ou igual a ${match[1]}`,
  },
  {
    match: /^Input should be less than or equal to (.+)$/i,
    format: (match) => `deve ser menor ou igual a ${match[1]}`,
  },
];

function humanizeFieldName(fieldName) {
  return String(fieldName || '')
    .replace(/_/g, ' ')
    .replace(/\bid\b/gi, 'ID')
    .replace(/^./, (value) => value.toUpperCase());
}

function translateValidationMessage(message) {
  if (!message || typeof message !== 'string') return 'valor inválido';

  for (const translation of VALIDATION_MESSAGE_TRANSLATIONS) {
    const match = message.match(translation.match);
    if (match) {
      return translation.format(match);
    }
  }

  return message;
}

function formatValidationEntry(entry, options = {}) {
  if (typeof entry === 'string') return entry;

  const fieldLabels = options.fieldLabels || {};
  const locationParts = Array.isArray(entry?.loc) ? entry.loc.filter(Boolean) : [];
  const fieldKey = locationParts[locationParts.length - 1];
  const fieldLabel = fieldLabels[fieldKey] || humanizeFieldName(fieldKey);
  const translatedMessage = options.translateValidation
    ? translateValidationMessage(entry?.msg)
    : entry?.msg;

  if (!fieldLabel) return translatedMessage || 'Erro de validação';
  return `${fieldLabel}: ${translatedMessage || 'Erro de validação'}`;
}

function getRequestLabel(error) {
  return String(error?.__safeMeta?.endpoint || error?.__requestMeta?.url || '').trim();
}

function isCloudflareOriginError(error) {
  const status = Number(error?.response?.status || 0);
  if ([502, 503, 504, 522, 524].includes(status)) {
    return true;
  }

  const responseData = error?.response?.data;
  const responseText = typeof responseData === 'string'
    ? responseData
    : (typeof responseData?.detail === 'string' ? responseData.detail : '');
  const normalized = String(responseText || '').toLowerCase();

  return normalized.includes('cloudflare')
    && (normalized.includes('origin') || normalized.includes('web server'));
}

function isAbortLikeError(error) {
  const code = String(error?.code || '').toLowerCase();
  const message = String(error?.message || '').toLowerCase();
  return code === 'err_canceled'
    || message.includes('aborted')
    || message.includes('aborterror')
    || message.includes('canceled');
}

export function normalizeApiError(err, fallbackMessage = 'Erro inesperado', options = {}) {
  const safeMeta = err?.__safeMeta;
  const requestLabel = getRequestLabel(err);

  if (isCloudflareOriginError(err)) {
    return 'O servidor de origem respondeu com erro de infraestrutura. Tente novamente em instantes.';
  }

  if (isAbortLikeError(err)) {
    return 'Pedido cancelado antes de terminar.';
  }

  if (safeMeta?.endpoint) {
    if (safeMeta.timedOut) {
      return 'O pedido demorou mais do que o esperado. Tente actualizar esta secção.';
    }
    if (safeMeta.httpStatus) {
      if (safeMeta.httpStatus === 404) {
        return safeMeta.errorMessage || fallbackMessage;
      }
      return `${safeMeta.endpoint}: ${safeMeta.errorMessage || `HTTP ${safeMeta.httpStatus}`}`;
    }
    if (safeMeta.errorMessage) {
      return `${safeMeta.endpoint}: ${safeMeta.errorMessage}`;
    }
  }

  const status = Number(err?.response?.status || 0);
  if (status === 404 && options.optional !== false) {
    return fallbackMessage;
  }

  const data = err?.response?.data;
  const detail = data?.detail ?? data;

  if (!detail) {
    if (requestLabel && err?.__requestMeta?.status) {
      return `${requestLabel}: HTTP ${err.__requestMeta.status}`;
    }
    return err?.message || fallbackMessage;
  }
  if (typeof detail === 'string') return detail;

  if (Array.isArray(detail)) {
    const messages = detail.map((entry) => formatValidationEntry(entry, options));
    return messages.join(' | ');
  }

  if (typeof detail === 'object' && detail?.msg) {
    return formatValidationEntry(detail, options);
  }
  if (typeof detail === 'object' && detail?.message) return String(detail.message);

  try {
    return JSON.stringify(detail);
  } catch {
    return fallbackMessage;
  }
}